import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Accordion from '../../../shared/Accordion';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import { Pill } from '../../../shared/Pill';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDate } from '../../../shared/tenantClock';
import '../../../shared/StyledInput.css';
import AccountApplicationDrawer from './AccountApplicationDrawer';
import { askConfirm } from '../../../shared/AppDialogs';

// Trading and Credit Account Applications (issue #290): staff send a
// business a private link to apply online, then review, approve or decline
// what comes back (AccountApplicationDrawer). A Trading Account Application
// goes from a Prospect at Qualified or Opportunity, or from a Customer to
// get a signed version on file; a Credit Account Application goes from a
// Customer to a Director / Proprietor named on its approved Trading Account
// Application. The card's header line shows which versions of the terms the
// Customer signed, by whom and when. Shown on any Prospect or Customer.
// An emailed to-do's link (?application=ID) opens that application.
// Backend: accountApplicationService.js.

export const APPLICATION_STATUS = {
  sent: { label: 'Sent', color: '#1d4ed8', background: '#dbeafe' },
  submitted: { label: 'Submitted', color: '#92400e', background: '#fef3c7' },
  approved: { label: 'Approved', color: '#166534', background: '#dcfce7' },
  declined: { label: 'Declined', color: '#991b1b', background: '#fee2e2' },
  expired: { label: 'Expired', color: '#475569', background: '#e2e8f0' },
  cancelled: { label: 'Cancelled', color: '#475569', background: '#e2e8f0' },
};

const KIND_LABELS = { trading: 'Trading Account Application', credit: 'Credit Account Application' };
const rowStyle = { position: 'relative', marginBottom: 14, paddingBottom: 12, paddingRight: 28, borderBottom: '1px solid #eef0f3' };
const menuStyle = { position: 'absolute', top: -4, right: -6 };
const LABEL_STYLE = { fontSize: 12, color: '#5a6b85', display: 'block', marginBottom: 4 };
const MUTED = { fontSize: 12, color: '#5a6b85' };

export function StatusPill({ status }) {
  const meta = APPLICATION_STATUS[status] || APPLICATION_STATUS.cancelled;
  return <Pill color={meta.color} background={meta.background}>{meta.label}</Pill>;
}

function SignedLine({ title, entry }) {
  if (!entry) return null;
  const by = entry.signer ? ` · signed by ${entry.signer.name} on ${formatDate(entry.signer.signed_at)}` : '';
  return <div style={MUTED}>{title} version {entry.version}{by}</div>;
}

function AccountApplicationsCard({ entityId, entity, isLifecycle, onEntityChanged }) {
  const { canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null); // { applications, send_options, signed, send_environment }
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(null); // 'trading' | 'credit' while its form is open
  const [email, setEmail] = useState('');
  const [directorIndex, setDirectorIndex] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [reviewId, setReviewId] = useState(() => Number(new URLSearchParams(window.location.search).get('application')) || null);

  // Only a business applies for an account; an Individual has no card.
  const isBusiness = entity?.type === 'organization';

  useEffect(() => {
    if (!isBusiness) return undefined;
    let cancelled = false;
    axios.get(`/api/commercial-entities/${entityId}/applications`)
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => { if (!cancelled) showError(err, 'Could not load applications'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entity]);

  // An emailed link opens its application once, then leaves the address.
  useEffect(() => {
    if (!reviewId) return;
    setOpen(true);
    const params = new URLSearchParams(window.location.search);
    if (params.has('application')) {
      params.delete('application');
      const query = params.toString();
      navigate(`${window.location.pathname}${query ? `?${query}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isBusiness || !data) return null;
  const { applications, send_options: options, signed, send_environment: sendEnvironment } = data;
  if (!isLifecycle && applications.length === 0) return null;

  const run = async (request, fallback) => {
    setSaving(true);
    try {
      const res = await request();
      setData(res.data.data);
      setNotice(res.data.message || '');
      dismissError();
      return true;
    } catch (err) {
      showError(err, fallback);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startSend = (kind) => {
    setSending(kind);
    setEmail(kind === 'trading' && options.emails.length === 1 ? options.emails[0].email : '');
    setDirectorIndex(kind === 'credit' && options.directors.length === 1 ? String(options.directors[0].index) : '');
    setNotice('');
    setOpen(true);
  };

  const submitSend = async (e) => {
    e.preventDefault();
    const ok = await run(
      () => axios.post(`/api/commercial-entities/${entityId}/applications`, { kind: sending, email, director_index: directorIndex }),
      'Application not sent',
    );
    if (ok) {
      setSending(null);
      onEntityChanged({ activitiesOnly: true });
    }
  };

  const cancelLink = async (app) => {
    if (!(await askConfirm(`Cancel the link sent to ${app.email}? It stops working straight away.`, { confirmLabel: 'Cancel Link', destructive: true }))) return;
    run(() => axios.post(`/api/commercial-entities/${entityId}/applications/${app.id}/cancel`), 'Cancel failed');
  };
  const resend = (app) => run(() => axios.post(`/api/commercial-entities/${entityId}/applications/${app.id}/resend`), 'Link not sent')
    .then((ok) => ok && onEntityChanged({ activitiesOnly: true }));

  const menuFor = (app) => {
    const items = [{ key: 'open', label: 'Open', onClick: () => setReviewId(app.id) }];
    if (canUpdate && app.status === 'sent') {
      items.push({ key: 'resend', label: 'Send a fresh link', onClick: () => resend(app), disabled: saving });
      items.push({ key: 'cancel', label: 'Cancel link', onClick: () => cancelLink(app), disabled: saving, danger: true });
    }
    if (canUpdate && ['expired', 'cancelled'].includes(app.status)) {
      items.push({ key: 'resend', label: 'Send a fresh link', onClick: () => resend(app), disabled: saving || !!options.reason });
    }
    return items;
  };

  const can = (kind) => options.kinds.includes(kind);
  const menuItems = canUpdate ? [
    {
      key: 'send-trading',
      label: 'Send Trading Account Application',
      onClick: () => startSend('trading'),
      disabled: !can('trading'),
      disabledTitle: options.reason || undefined,
    },
    entity && (entity.relationships || []).some((r) => r.relationship_type?.system_key === 'customer') && {
      key: 'send-credit',
      label: 'Send Credit Account Application',
      onClick: () => startSend('credit'),
      disabled: !can('credit'),
      disabledTitle: options.reason || 'Approve a Trading Account Application first: credit goes to a Director / Proprietor named on it',
    },
  ].filter(Boolean) : undefined;

  const canSend = sending === 'trading' ? !!email.trim() : directorIndex !== '';

  return (
    <>
      <Accordion title={`Applications (${applications.length})`} open={open} onToggle={() => setOpen((o) => !o)} menuItems={menuItems}>
        {(signed.terms_of_trade || signed.credit_terms) && (
          <div style={{ ...rowStyle, paddingRight: 0 }}>
            <SignedLine title="Terms & Conditions of Trade" entry={signed.terms_of_trade} />
            <SignedLine title="Credit Terms" entry={signed.credit_terms} />
          </div>
        )}
        {notice && <div style={{ fontSize: 13, color: '#1b5e20', marginBottom: 10 }}>{notice}</div>}
        {sending && (
          <form onSubmit={submitSend} style={{ ...rowStyle, paddingRight: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Send {KIND_LABELS[sending]}</div>
            {sending === 'trading' ? (
              <>
                <label style={LABEL_STYLE} htmlFor="application-email">Email to send it to</label>
                <input
                  id="application-email"
                  className="styled-input"
                  type="email"
                  list="application-emails"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%' }}
                  autoFocus
                />
                <datalist id="application-emails">
                  {options.emails.map((o) => <option key={o.email} value={o.email}>{o.name}</option>)}
                </datalist>
                <div style={{ ...MUTED, marginTop: 4 }}>
                  The form opens pre-filled with this business&apos;s details, and this person as Primary Contact if they&apos;re on the record.
                </div>
              </>
            ) : (
              <>
                <label style={LABEL_STYLE} htmlFor="application-director">Director / Proprietor</label>
                <select id="application-director" className="styled-input" value={directorIndex} onChange={(e) => setDirectorIndex(e.target.value)} style={{ width: '100%' }} autoFocus>
                  <option value="">Select...</option>
                  {options.directors.map((d) => (
                    <option key={d.index} value={d.index}>{d.first_name} {d.family_name} ({d.role}) — {d.email}</option>
                  ))}
                </select>
                <div style={{ ...MUTED, marginTop: 4 }}>Named on the approved Trading Account Application. The form opens pre-filled with their details.</div>
              </>
            )}
            <div style={{ ...MUTED, marginTop: 4 }}>The link works for 30 days, until it&apos;s used, or until you cancel it.</div>
            {sendEnvironment?.nonProduction && (
              <div style={{ fontSize: 12, color: '#8a5a00', background: '#fff8e6', border: '1px solid #f0d9a6', borderRadius: 4, padding: '8px 10px', marginTop: 8 }}>
                {sendEnvironment.testEmailAddress
                  ? <>Non-production: the email will be delivered to <strong>{sendEnvironment.testEmailAddress}</strong>.</>
                  : 'Non-production: sending is blocked until a Test Email Address is set on Platform Settings.'}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <SaveButton loading={saving} disabled={!canSend}>{saving ? 'Sending...' : 'Send'}</SaveButton>
              <CancelButton onClick={() => setSending(null)} disabled={saving} style={{ marginLeft: 8 }} />
            </div>
          </form>
        )}
        {applications.length === 0 && !sending && (
          <div style={{ fontSize: 13, color: '#999' }}>{options.reason && !options.reason.includes('already open') ? options.reason : 'No applications yet'}</div>
        )}
        {applications.map((app) => (
          <div key={app.id} style={rowStyle}>
            <div style={menuStyle}>
              <RowActionsMenu items={menuFor(app)} ariaLabel={`${app.label} actions`} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="link-button" onClick={() => setReviewId(app.id)} style={{ fontSize: 13, fontWeight: 600, textAlign: 'left' }}>
                {app.label}
              </button>
              <StatusPill status={app.status} />
            </div>
            <div style={{ ...MUTED, marginTop: 2, wordBreak: 'break-all' }}>
              Sent to {app.email} on {formatDate(app.sent_at)}{app.sent_by ? ` by ${app.sent_by}` : ''}
            </div>
            {app.status === 'sent' && <div style={MUTED}>Link works until {formatDate(app.expires_at)}</div>}
            {app.signature && <div style={MUTED}>Signed by {app.signature.name} on {formatDate(app.signature.signed_at)}</div>}
            {app.decided_at && <div style={MUTED}>{app.status === 'approved' ? 'Approved' : 'Declined'} on {formatDate(app.decided_at)}{app.decided_by ? ` by ${app.decided_by}` : ''}</div>}
          </div>
        ))}
      </Accordion>
      {reviewId && (
        <AccountApplicationDrawer
          entityId={entityId}
          entity={entity}
          applicationId={reviewId}
          onClose={() => setReviewId(null)}
          onChanged={(cardData, message) => {
            setData(cardData);
            setNotice(message || '');
            setOpen(true);
            onEntityChanged({});
          }}
        />
      )}
    </>
  );
}

export default AccountApplicationsCard;
